import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  MEDIA_API_BASE,
  MediaCopy,
  MediaCopyRequest,
  MediaFrameRequest,
  MediaItem,
  MediaList,
  MediaOrderRequest,
  MediaSiblings,
  UploadMedia,
  UploadMediaRequest
} from './media-api';

/**
 * `/api/vn/adm/product-media/**` as plain calls: the one place that knows the paths. Query parameters are snake_case. The login (a bearer token) is added by
 * the interceptor of the app to every `/api/` address; the address of the cloud session (`uploadUrl`) is not an `/api/` address and never comes through here.
 */
@Injectable({ providedIn: 'root' })
export class ProductMediaHttp {
  private readonly http = inject(HttpClient);

  list(productInfoId: number): Promise<MediaList> {
    return firstValueFrom(this.http.get<MediaList>(MEDIA_API_BASE, { params: new HttpParams().set('product_info_id', productInfoId) }));
  }

  /** The other sizes of the cards that contain this size, with their numbers (one call). */
  siblings(productInfoId: number): Promise<MediaSiblings> {
    return firstValueFrom(this.http.get<MediaSiblings>(`${MEDIA_API_BASE}/siblings`, { params: new HttpParams().set('product_info_id', productInfoId) }));
  }

  startUpload(request: UploadMediaRequest): Promise<UploadMedia> {
    return firstValueFrom(this.http.post<UploadMedia>(`${MEDIA_API_BASE}/uploads`, request));
  }

  finishUpload(id: number): Promise<MediaItem> {
    return firstValueFrom(this.http.post<MediaItem>(`${MEDIA_API_BASE}/${id}/finish`, {}));
  }

  setFrame(id: number, request: MediaFrameRequest): Promise<MediaItem> {
    return firstValueFrom(this.http.put<MediaItem>(`${MEDIA_API_BASE}/${id}/frame`, request));
  }

  reorder(request: MediaOrderRequest): Promise<MediaList> {
    return firstValueFrom(this.http.put<MediaList>(`${MEDIA_API_BASE}/order`, request));
  }

  async remove(id: number): Promise<void> {
    await firstValueFrom(this.http.delete<void>(`${MEDIA_API_BASE}/${id}`));
  }

  copy(request: MediaCopyRequest): Promise<MediaCopy> {
    return firstValueFrom(this.http.post<MediaCopy>(`${MEDIA_API_BASE}/copy`, request));
  }

  retry(id: number): Promise<MediaItem> {
    return firstValueFrom(this.http.post<MediaItem>(`${MEDIA_API_BASE}/${id}/retry`, {}));
  }

  /** A private preview (`previewUrl` / `stripUrl` of an item): an `<img>` cannot send the login, so the picture comes as a blob. */
  preview(url: string): Promise<Blob> {
    return firstValueFrom(this.http.get(url, { responseType: 'blob' }));
  }
}
